import { FormItem, FormItemsGroup, ModuleGroup, SkeletonText, Space, Text } from '@billy/design-system'
import { format, parseISO } from 'date-fns'
import compact from 'lodash/compact'
import sortBy from 'lodash/sortBy'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useDefaultCegidExpenseAccount } from '../../../../hooks/useDefaultCegidExpenseAccount'
import { useBillView } from './contexts/billViewContext'
import { BillHeader } from './elements/BillHeader'
import { BillLine } from './elements/BillLine'
import { BillSummary } from './elements/BillSummary'
import { BillTransactions } from './elements/BillTransactions'
import { BillVendor } from './elements/BillVendor'
import * as Styled from './styles'
import { billStatusToOutlineColor } from './utils/billStatusToOutlineColor'

export const BillReadableForm = (): ReactElement => {
  const { t } = useTranslation()
  const { shouldUseDefaultExpenseAccount } = useDefaultCegidExpenseAccount()
  const { billStatus, billData, isFetching } = useBillView()
  const { bill, billLines } = billData || {}

  const billDate = bill?.entryDate ? format(parseISO(bill?.entryDate), 'dd/MM/yyyy') : undefined
  const dueDate = bill?.dueDate ? format(parseISO(bill?.dueDate), 'dd/MM/yyyy') : undefined
  const moduleOutlineColor = billStatus ? billStatusToOutlineColor[billStatus] : undefined
  const referenceNo = bill?.suppliersInvoiceNo
  const lines = billLines ? sortBy(billLines, 'priority') : undefined

  return (
    <Styled.BillViewWrapper>
      <ModuleGroup>
        <Styled.Module outlineColor={moduleOutlineColor}>
          <BillHeader />

          <Space size="s8" />

          <BillVendor />
          <FormItemsGroup itemsInRow={compact([true, dueDate, referenceNo, true]).length}>
            <FormItem label={t('bill.view.form.bill_date')}>
              {isFetching ? <SkeletonText /> : <Text>{billDate && billDate}</Text>}
            </FormItem>
            {dueDate && (
              <FormItem label={t('bill.view.form.due_date')}>
                <Text>{dueDate}</Text>
              </FormItem>
            )}
            {referenceNo && (
              <FormItem label={t('bill.view.form.reference_number')}>
                <Text>{referenceNo}</Text>
              </FormItem>
            )}

            <BillTransactions />
          </FormItemsGroup>

          {lines?.length ? (
            lines.map((billLine) => {
              return (
                <BillLine
                  key={billLine.priority}
                  billLine={billLine}
                  shouldHideExpenseCategory={shouldUseDefaultExpenseAccount}
                />
              )
            })
          ) : (
            <BillLine />
          )}
        </Styled.Module>

        <BillSummary />
      </ModuleGroup>
    </Styled.BillViewWrapper>
  )
}
